import { useState } from 'react';
import { Plane, Calendar, Clock, DoorOpen, LayoutGrid } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/components/utils/utils';
import { useI18n } from '@/hooks/useI18n';
import { splitScheduledTime } from '@/lib/builder/utils/flight-lookup.utils';
import type { FlightLookupEndpoint, ResolvedFlightLookup } from '@/types/flight.types';

/** Stati per cui il volo e' "in salute": il resto va segnalato in rosso. */
const HEALTHY_STATUSES = new Set(['scheduled', 'active', 'en-route', 'landed']);

const statusVariant = (status?: string): 'success' | 'destructive' | 'secondary' => {
  if (!status) return 'secondary';
  const normalized = status.toLowerCase();
  if (normalized === 'cancelled' || normalized === 'incident' || normalized === 'diverted') {
    return 'destructive';
  }
  return HEALTHY_STATUSES.has(normalized) ? 'success' : 'secondary';
};

interface FlightPreviewCardProps {
  result: ResolvedFlightLookup;
  className?: string;
}

/**
 * Anteprima del volo trovato dal lookup, prima che il TD decida di usarlo.
 *
 * Componente di sola presentazione: non conosce la tratta su cui finira' il volo,
 * cosi' resta riusabile (oggi il tab "Cerca volo", domani una card di riepilogo).
 */
export const FlightPreviewCard = ({ result, className }: FlightPreviewCardProps) => {
  const { t, language } = useI18n();
  const [logoFailed, setLogoFailed] = useState(false);

  const { flight, airline } = result;
  const showLogo = Boolean(airline.logoUrl) && !logoFailed;

  const formatDate = (isoDate: string): string => {
    if (!isoDate) return '—';
    const [year, month, day] = isoDate.split('-').map(Number);
    const parsed = new Date(year, (month || 1) - 1, day || 1);
    if (Number.isNaN(parsed.getTime())) return isoDate;
    return parsed.toLocaleDateString(language, { day: '2-digit', month: 'short' });
  };

  const renderEndpoint = (endpoint: FlightLookupEndpoint, label: string) => {
    const { date, time } = splitScheduledTime(endpoint.scheduledTime);

    return (
      <div className="space-y-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold text-gray-900">{endpoint.airportIata || '—'}</span>
          <span className="truncate text-xs text-gray-500">{endpoint.city}</span>
        </div>
        <p className="truncate text-xs text-gray-600">{endpoint.airportName}</p>
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3 shrink-0" />
            {formatDate(date)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3 shrink-0" />
            {time || '—'}
          </span>
        </div>
        {(endpoint.terminal || endpoint.gate) && (
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {endpoint.terminal && (
              <span className="flex items-center gap-1">
                <LayoutGrid className="h-3 w-3 shrink-0" />
                {t('modals.flightLookup.terminal')} {endpoint.terminal}
              </span>
            )}
            {endpoint.gate && (
              <span className="flex items-center gap-1">
                <DoorOpen className="h-3 w-3 shrink-0" />
                {t('modals.flightLookup.gate')} {endpoint.gate}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={cn('space-y-3 rounded-lg border border-gray-200 p-4', className)}>
      {/* Intestazione: compagnia, numero volo e stato sulla stessa riga. */}
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
          style={airline.color && !showLogo ? { backgroundColor: airline.color } : undefined}
        >
          {showLogo ? (
            <img
              src={airline.logoUrl as string}
              alt={airline.name}
              className="h-full w-full object-contain"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <Plane className={cn('h-4 w-4', airline.color ? 'text-white' : 'text-gray-500')} />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">{airline.name}</p>
          <p className="text-xs text-gray-500">
            {flight.flight.iata}
            {flight.aircraft?.model && <> · {flight.aircraft.model}</>}
          </p>
        </div>

        {flight.flight.status && (
          <Badge variant={statusVariant(flight.flight.status)} className="shrink-0">
            {t(`modals.flightLookup.status.${flight.flight.status.toLowerCase()}`, {
              defaultValue: flight.flight.status,
            })}
          </Badge>
        )}
      </div>

      {/* Tratta: due colonne da tablet in su, una sola su mobile. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {renderEndpoint(flight.departure, t('modals.flightLookup.departure'))}
        {renderEndpoint(flight.arrival, t('modals.flightLookup.arrival'))}
      </div>
    </div>
  );
};

export default FlightPreviewCard;
